import type { CustomerOrderSearchParams, CustomerPurchaseOrderSearchParams, SalesOrderSearchParams } from "./schemas"
import { ProductSelectionTab } from "./types"

import type { FileTypeToChipMap } from "@/components/Tables/Aeros/hooks/useFileColumns"
import { PermissionActionType } from "@/constants/permissions"
import { FREIGHT_TERMS_TO_LABEL } from "@/constants/shippingTerms"
import {
  CustomerOrderDateRangeType,
  CustomerPurchaseOrderDateRangeType,
  CustomerPurchaseOrderStatus,
  DeliveryDateReference,
  FieldGroupEnum,
  type FreightTerms,
  SalesOrderDateRangeType,
  SalesOrderStatus,
  ShipStatus,
} from "@/graphql/codegen/graphql"
import colors from "@/styles/colors"
import type { Option } from "@/types/form-fields"

export const CUSTOMER_ORDER_ROUTE = "/app/orders/" as const

export const CUSTOMER_ORDER_ACCESS_PERMISSIONS: PermissionActionType[] = [
  PermissionActionType.SALES_ORDER_VIEW,
  PermissionActionType.SALES_ORDER_CREATION,
]

export const CUSTOMER_ORDER_DELIVERY_DATE_REFERENCE_TO_LABEL: Record<DeliveryDateReference, string> = {
  [DeliveryDateReference.ShipBy]: "Shipped Date",
  [DeliveryDateReference.DeliverBy]: "Deliver By",
} as const

export const CUSTOMER_ORDER_DATE_RANGE_TYPE_TO_LABEL: Record<CustomerOrderDateRangeType, string> = {
  [CustomerOrderDateRangeType.DeliveryDate]: "Expected Delivery Date",
  [CustomerOrderDateRangeType.ShipByDate]: "Shipped Date",
} as const

// The Customer Orders list filter and the Sales Order preview filter use separate date-range-type
// enums with no shared values, so the list's URL filter is mapped to its closest SO-side equivalent
// when driving the embedded Sales Order preview query.
export const CUSTOMER_ORDER_DATE_RANGE_TYPE_TO_SALES_ORDER_DATE_RANGE_TYPE: Record<
  CustomerOrderDateRangeType,
  SalesOrderDateRangeType
> = {
  [CustomerOrderDateRangeType.DeliveryDate]: SalesOrderDateRangeType.RequestedDeliveryDate,
  [CustomerOrderDateRangeType.ShipByDate]: SalesOrderDateRangeType.RequestedShipDate,
} as const

export const CUSTOMER_ORDER_FILTER_KEYS: (keyof CustomerOrderSearchParams)[] = [
  "statuses",
  "dateFrom",
  "dateTo",
  "dateRangeType",
]

export const CUSTOMER_ORDER_DATE_RANGE_FILTER_KEYS: (keyof CustomerOrderSearchParams)[] = [
  "dateFrom",
  "dateTo",
  "dateRangeType",
]

export const CUSTOMER_PURCHASE_ORDER_STATUS_TO_LABEL: Record<CustomerPurchaseOrderStatus, string> = {
  [CustomerPurchaseOrderStatus.Open]: "Open",
  [CustomerPurchaseOrderStatus.Complete]: "Complete",
  [CustomerPurchaseOrderStatus.Cancelled]: "Cancelled",
  [CustomerPurchaseOrderStatus.PartiallyShipped]: "Partially Shipped",
  [CustomerPurchaseOrderStatus.Shipped]: "Shipped",
} as const

export const CUSTOMER_PURCHASE_ORDER_DATE_RANGE_TYPE_TO_LABEL: Record<CustomerPurchaseOrderDateRangeType, string> = {
  [CustomerPurchaseOrderDateRangeType.DeliveryDate]: "Expected Delivery Date",
  [CustomerPurchaseOrderDateRangeType.ShipByDate]: "Shipped Date",
} as const

export const CUSTOMER_PURCHASE_ORDER_FILTER_KEYS: (keyof CustomerPurchaseOrderSearchParams)[] = [
  "statuses",
  "dateFrom",
  "dateTo",
  "dateRangeType",
]

export const CUSTOMER_PURCHASE_ORDER_DATE_RANGE_FILTER_KEYS: (keyof CustomerPurchaseOrderSearchParams)[] = [
  "dateFrom",
  "dateTo",
  "dateRangeType",
]

export const ORDER_TABS = [
  {
    label: "Customer Purchase Orders",
    value: "customer-purchase-orders",
  },
  {
    label: "Open Sales Orders",
    value: "open-sales-orders",
  },
  {
    label: "Closed Sales Orders",
    value: "closed-sales-orders",
  },
] as const

export const SALES_ORDER_STATUS_TO_LABEL: Record<SalesOrderStatus, string> = {
  [SalesOrderStatus.Open]: "Open",
  [SalesOrderStatus.Confirmed]: "Confirmed",
  [SalesOrderStatus.Completed]: "Completed",
  [SalesOrderStatus.Canceled]: "Cancelled",
  [SalesOrderStatus.AwaitingPrepayment]: "Awaiting Prepayment",
  [SalesOrderStatus.Invoiced]: "Invoiced",
  [SalesOrderStatus.Paid]: "Paid",
  [SalesOrderStatus.ReconfirmationNeeded]: "Reconfirmation Needed",
} as const

// Manual transitions offered in the Update Status dialog. OPEN exits only via Send SO Confirmation or
// Cancel, RECONFIRMATION_NEEDED is set by the system on edit and left only via Send SO Confirmation,
// AWAITING_PREPAYMENT is set by the system at confirmation and never offered, and CANCELED is reached
// only via Cancel Sales Order.
export const SALES_ORDER_STATUS_TO_TARGET_STATUSES: Record<SalesOrderStatus, SalesOrderStatus[]> = {
  [SalesOrderStatus.Open]: [],
  [SalesOrderStatus.AwaitingPrepayment]: [
    SalesOrderStatus.Confirmed,
    SalesOrderStatus.Completed,
    SalesOrderStatus.Invoiced,
    SalesOrderStatus.Paid,
  ],
  [SalesOrderStatus.Confirmed]: [SalesOrderStatus.Completed, SalesOrderStatus.Invoiced, SalesOrderStatus.Paid],
  [SalesOrderStatus.ReconfirmationNeeded]: [
    SalesOrderStatus.Completed,
    SalesOrderStatus.Invoiced,
    SalesOrderStatus.Paid,
  ],
  [SalesOrderStatus.Completed]: [SalesOrderStatus.Confirmed, SalesOrderStatus.Invoiced, SalesOrderStatus.Paid],
  [SalesOrderStatus.Invoiced]: [SalesOrderStatus.Confirmed, SalesOrderStatus.Completed, SalesOrderStatus.Paid],
  [SalesOrderStatus.Paid]: [SalesOrderStatus.Confirmed, SalesOrderStatus.Completed, SalesOrderStatus.Invoiced],
  [SalesOrderStatus.Canceled]: [],
}

export const SALES_ORDER_EDITABLE_STATUSES: SalesOrderStatus[] = [
  SalesOrderStatus.Open,
  SalesOrderStatus.AwaitingPrepayment,
  SalesOrderStatus.Confirmed,
  SalesOrderStatus.ReconfirmationNeeded,
]

export const SALES_ORDER_CONFIRMABLE_STATUSES: SalesOrderStatus[] = [
  SalesOrderStatus.Open,
  SalesOrderStatus.ReconfirmationNeeded,
]

export const SALES_ORDER_CANCELLABLE_STATUSES: SalesOrderStatus[] = SALES_ORDER_EDITABLE_STATUSES

export const SALES_ORDER_DATE_RANGE_TYPE_TO_LABEL: Record<SalesOrderDateRangeType, string> = {
  [SalesOrderDateRangeType.ConfirmationSentDate]: "Confirmation Sent Date",
  [SalesOrderDateRangeType.PromisedShipDate]: "Promised Ship Date",
  [SalesOrderDateRangeType.RequestedShipDate]: "Requested Ship Date",
  [SalesOrderDateRangeType.RequestedDeliveryDate]: "Requested Delivery Date",
  [SalesOrderDateRangeType.WarehouseShippedDate]: "Warehouse Shipped Date",
} as const

export const SALES_ORDER_FILTER_KEYS: (keyof SalesOrderSearchParams)[] = [
  "statuses",
  "dateFrom",
  "dateTo",
  "dateRangeType",
]

export const SALES_ORDER_DATE_RANGE_FILTER_KEYS: (keyof SalesOrderSearchParams)[] = [
  "dateFrom",
  "dateTo",
  "dateRangeType",
]

export const SALES_ORDER_PRESERVED_EMPTY_FILTER_KEYS: (keyof SalesOrderSearchParams)[] = ["statuses"]

export const SALES_ORDER_DEFAULT_DATE_RANGE_DAYS = 30

export const CUSTOMER_ORDER_STATUS_OPTIONS: Option<SalesOrderStatus>[] = Object.values(SalesOrderStatus).map(
  (status) => ({ value: status, label: SALES_ORDER_STATUS_TO_LABEL[status] })
)

export const CUSTOMER_ORDER_DATE_RANGE_TYPE_OPTIONS: Option<CustomerOrderDateRangeType>[] = Object.values(
  CustomerOrderDateRangeType
).map((type) => ({ value: type, label: CUSTOMER_ORDER_DATE_RANGE_TYPE_TO_LABEL[type] }))

export const CUSTOMER_PURCHASE_ORDER_STATUS_OPTIONS: Option<CustomerPurchaseOrderStatus>[] = Object.values(
  CustomerPurchaseOrderStatus
).map((status) => ({ value: status, label: CUSTOMER_PURCHASE_ORDER_STATUS_TO_LABEL[status] }))

export const CUSTOMER_PURCHASE_ORDER_DATE_RANGE_TYPE_OPTIONS: Option<CustomerPurchaseOrderDateRangeType>[] =
  Object.values(CustomerPurchaseOrderDateRangeType).map((type) => ({
    value: type,
    label: CUSTOMER_PURCHASE_ORDER_DATE_RANGE_TYPE_TO_LABEL[type],
  }))

export const SALES_ORDER_STATUS_OPTIONS: Option<SalesOrderStatus>[] = Object.values(SalesOrderStatus).map((status) => ({
  value: status,
  label: SALES_ORDER_STATUS_TO_LABEL[status],
}))

export const SALES_ORDER_DATE_RANGE_TYPE_OPTIONS: Option<SalesOrderDateRangeType>[] = Object.values(
  SalesOrderDateRangeType
).map((type) => ({ value: type, label: SALES_ORDER_DATE_RANGE_TYPE_TO_LABEL[type] }))

export const SALES_ORDER_SHIPMENT_STATUS_TO_LABEL: Record<ShipStatus, string> = {
  [ShipStatus.Pending]: "Pending",
  [ShipStatus.InTransit]: "In Transit",
  [ShipStatus.Delivered]: "Delivered",
} as const

export const SALES_ORDER_SHIPMENT_STATUS_OPTIONS: Option<ShipStatus>[] = Object.values(ShipStatus).map((status) => ({
  value: status,
  label: SALES_ORDER_SHIPMENT_STATUS_TO_LABEL[status],
}))

export const SALES_ORDER_FREIGHT_TERMS_OPTIONS: Option<FreightTerms>[] = Object.entries(FREIGHT_TERMS_TO_LABEL).map(
  ([value, label]) => ({ value: value as FreightTerms, label })
)

export const FREIGHT_QUOTE_NUMBER_MAX_LENGTH = 20

export const FREIGHT_LINE_DEFAULT_QUANTITY = 1

export const FREIGHT_LINE_UNIT_NAMES = ["each", "ea"]

export const PRODUCT_SELECTION_TABS = [
  { label: "Approved", value: ProductSelectionTab.Approved },
  { label: "Sampled", value: ProductSelectionTab.Sampled },
  { label: "Quoted", value: ProductSelectionTab.Quoted },
  { label: "Linked Companies", value: ProductSelectionTab.LinkedCompanies },
] as const

export const APPROVED_PRODUCTS_PAGE_SIZE = 20

export const QUOTED_PRODUCTS_PAGE_SIZE = 20

export const SALES_ORDER_FILE_TYPE_MAPPING: FileTypeToChipMap = {
  [FieldGroupEnum.Cpo]: {
    label: "CPO",
    color: colors.primary["200"],
  },
  [FieldGroupEnum.Asn]: {
    label: "ASN",
    color: colors.tertiary.purple["300"],
  },
  [FieldGroupEnum.BillOfLading]: {
    label: "Bill of Lading",
    color: colors.tertiary.purple["300"],
  },
  [FieldGroupEnum.BrokerDoc]: {
    label: "Broker Documentation",
    color: colors.tertiary.orange["300"],
  },
  [FieldGroupEnum.OriginCert]: {
    label: "Certificate of Origin",
    color: colors.tertiary.yellow["300"],
  },
  [FieldGroupEnum.Coa]: {
    label: "COA",
    color: colors.tertiary.green["100"],
  },
  [FieldGroupEnum.CommercialDoc]: {
    label: "Commercial Documents",
    color: colors.primary["200"],
  },
  [FieldGroupEnum.Confirmation]: {
    label: "Confirmation",
    color: colors.tertiary.green["100"],
  },
  [FieldGroupEnum.DangerousGoodsDeclaration]: {
    label: "Dangerous Goods Declaration",
    color: colors.secondary["200"],
  },
  [FieldGroupEnum.DeliveryDocs]: {
    label: "Delivery Docs",
    color: colors.tertiary.purple["300"],
  },
  [FieldGroupEnum.EntrySummary]: {
    label: "Entry Summary",
    color: colors.tertiary.orange["300"],
  },
  [FieldGroupEnum.FumigationCert]: {
    label: "Fumigation Certificate",
    color: colors.tertiary.yellow["300"],
  },
  [FieldGroupEnum.HeatTreatmentCert]: {
    label: "Heat Treatment Certificate",
    color: colors.tertiary.yellow["300"],
  },
  [FieldGroupEnum.Isf]: {
    label: "ISF",
    color: colors.tertiary.orange["300"],
  },
  [FieldGroupEnum.LogisticsInvoice]: {
    label: "Logistics Invoice",
    color: colors.tertiary.pink["300"],
  },
  [FieldGroupEnum.Miscellaneous]: {
    label: "Misc Upload",
    color: colors.grey["300"],
  },
  [FieldGroupEnum.PackingList]: {
    label: "Packing List",
    color: colors.tertiary.purple["300"],
  },
  [FieldGroupEnum.CustomerInvoice]: {
    label: "Customer Invoice",
    color: colors.tertiary.pink["300"],
  },
}

export const SALES_ORDER_FILE_FIELD_GROUPS = Object.keys(SALES_ORDER_FILE_TYPE_MAPPING) as FieldGroupEnum[]

export const SALES_ORDER_DOCUMENT_TYPE_OPTIONS: Option<FieldGroupEnum>[] = Object.entries(
  SALES_ORDER_FILE_TYPE_MAPPING
).map(([value, { label }]) => ({ value: value as FieldGroupEnum, label }))

// Keys the BE reports in missingFields when a confirmation lacks required data
export const SALES_ORDER_MISSING_FIELD_TO_LABEL: Record<string, string> = {
  incoterms: "Incoterms",
  incotermsLocationId: "Incoterms Location",
  shipViaCompanyId: "Ship Via",
  freightTerms: "Freight",
}

export const UOM_ID_TO_SYMBOL: Record<string, string> = {
  USSTD_WEIGHT_POUND: "lb",
  USSTD_WEIGHT_SHORT_TON: "ton",
  METRIC_WEIGHT_KILOGRAM: "kg",
  METRIC_WEIGHT_TON: "t",
}

// The BE returns this instead of a date when a CPO's lines have different ship dates.
export const SHIPPED_DATE_VARIES = "Varies"
